import { addReportAPI } from '@/services/report.js';
import { getRepostLitThunk, updateConversionReportId } from '@/store/modules/globalSlice.js';
import { useRequest } from 'ahooks';
import {
  Button,
  Checkbox,
  Col,
  Modal,
  notification,
  Popover,
  Progress,
  Row,
  Space,
  Tooltip,
} from 'antd';
import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import './RiskBubble.css';

import lightCry from '@/assets/expression/light/cry.png';
import lightLaugh from '@/assets/expression/light/laugh.png';
import lightLaughHeartily from '@/assets/expression/light/laughHeartily.png';
import lightNone from '@/assets/expression/light/none.png';

import darkCry from '@/assets/expression/dark/cry.png';
import darkLaugh from '@/assets/expression/dark/laugh.png';
import darkLaughHeartily from '@/assets/expression/dark/laughHeartily.png';
import darkNone from '@/assets/expression/dark/none.png';
import dunpaiSvg from '@/assets/dunpai.svg';
import diamondSvg from '@/assets/diamond.svg';
import { setCurrentReportId } from '@/store/modules/reportSlice.js';
const RiskBubble = props => {
  const { themeMode } = useSelector(state => state.global);
  const { isMobile } = useOutletContext();
  const { score, content, ctas = [], isComplete, isReevaluate, level } = props;
  const { externalRef } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reportSelectionVisible, setReportSelectionVisible] = useState(false);
  const [selectedCtas, setSelectedCtas] = useState([]);
  const { conversionList } = useSelector(state => state.global);
  const { loading, runAsync: runAddReport } = useRequest(addReportAPI, { manual: true });
  const { t } = useTranslation(['chat', 'translation']);
  const [reportShowTag, setreportShowTag] = useState(false);

  useEffect(() => {
    if (Array.isArray(ctas) && ctas.length > 0) {
      const allCtasHaveRelaTag = ctas.every(item => {
        if (typeof item === 'object' && Object.prototype.hasOwnProperty.call(item, 'ctaRelaTag')) {
          return item.ctaRelaTag === true;
        }
        return false;
      });

      if (allCtasHaveRelaTag) {
        setreportShowTag(true);
      }
    }
  }, []);

  const addReport = async () => {
    if (!externalRef) return;
    let ctaIdList = [];
    if (ctas.length === 1) {
      if (!ctas[0].idCta) {
        notification.error({
          // 3. 使用 t 函数 (translation 命名空间)
          message: t('translation:error'),
          // 3. 使用 t 函数 (chat 命名空间)
          description: t('chat:riskBubble.notification.noCta'),
        });
        return;
      } else {
        ctaIdList = [ctas[0].idCta];
      }
    } else ctaIdList = selectedCtas;

    try {
      const reportId = await runAddReport({ chatId: externalRef, ctaIdList });
      dispatch(updateConversionReportId({ conversionId: externalRef, reportId }));
      dispatch(setCurrentReportId(reportId));
      dispatch(getRepostLitThunk()).then(() => {
        navigate(`/report/${reportId}`);
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleOk = () => {
    addReport();
    handleCancel();
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedCtas([]);
  };

  const handleSelectionOk = () => {
    setReportSelectionVisible(false);
    setIsModalOpen(true);
  };

  const handleSelectionCancel = () => {
    setReportSelectionVisible(false);
    handleCancel();
  };

  const handleReport = () => {
    if (ctas.length > 1) {
      setReportSelectionVisible(true);
    } else {
      setIsModalOpen(true);
    }
  };

  const getCurrentConversion = () => {
    const item = conversionList.find(item => item.externalRef === externalRef);
    const flag = item?.reportId != null || item?.isWhitelistFlag == 1;
    return flag;
  };

  const reportPopoverContent = (
    <div style={{ maxWidth: 400, color: 'white', fontSize: 14, lineHeight: 1.6 }}>
      <p dangerouslySetInnerHTML={{ __html: t('chat:riskBubble.reportPopover.line1') }} />
      <br />
      <p dangerouslySetInnerHTML={{ __html: t('chat:riskBubble.reportPopover.line2') }} />
      <br />
      <p dangerouslySetInnerHTML={{ __html: t('chat:riskBubble.reportPopover.line3') }} />
    </div>
  );

  const renderSelections = () => {
    const dynamicWidth = () => {
      return `clamp(14rem, calc(12rem + 20vw), 24rem)`;
    };

    return (
      <Checkbox.Group style={{ width: '80%' }} value={selectedCtas} onChange={setSelectedCtas}>
        <Row justify="start">
          {ctas.map((item, index) => {
            return (
              <Col span={24} key={index} style={{ textAlign: 'start', marginBottom: '.3rem' }}>
                {item.isWhite ? (
                  <Space>
                    <span>
                      <img
                        src={diamondSvg}
                        alt="diamond"
                        style={{ width: '1.5em', height: '1.5em', verticalAlign: 'middle' }}
                      />
                    </span>
                    <span>{item.cta}</span>
                  </Space>
                ) : (
                  <>
                    {!item.ctaRelaTag && (
                      <Checkbox value={item.idCta}>
                        <div style={{ width: dynamicWidth(), overflow: 'auto' }}>{item.cta}</div>
                      </Checkbox>
                    )}
                  </>
                )}
              </Col>
            );
          })}
        </Row>
      </Checkbox.Group>
    );
  };
  // Convert color hexadecimal to RGB
  const hexToRgb = hex => {
    const match = /^#([a-fA-F0-9]{6})$/.exec(hex);
    if (!match) return null;
    const [r, g, b] = match[1].match(/.{2}/g).map(x => parseInt(x, 16));
    return { r, g, b };
  };

  // Convert RGB to hexadecimal
  const rgbToHex = rgb => {
    return `#${((rgb.r << 16) | (rgb.g << 8) | rgb.b).toString(16).padStart(6, '0').toUpperCase()}`;
  };

  // Calculate interpolation between two colors
  const interpolateColor = (startColor, endColor, ratio) => {
    const r = Math.round(startColor.r + (endColor.r - startColor.r) * ratio);
    const g = Math.round(startColor.g + (endColor.g - startColor.g) * ratio);
    const b = Math.round(startColor.b + (endColor.b - startColor.b) * ratio);
    return { r, g, b };
  };

  // Get gradient color
  const getGradientColorAtPercent = percent => {
    const colorStops = [
      { threshold: 0, color: '#61C26E' },
      { threshold: 25, color: '#E9E138' },
      { threshold: 75, color: '#F79122' },
      { threshold: 100, color: '#FF0000' },
    ];

    let startColor, endColor, ratio;

    for (let i = 0; i < colorStops.length - 1; i++) {
      const start = colorStops[i];
      const end = colorStops[i + 1];
      if (percent >= start.threshold && percent <= end.threshold) {
        startColor = hexToRgb(start.color);
        endColor = hexToRgb(end.color);
        ratio = (percent - start.threshold) / (end.threshold - start.threshold);
        return rgbToHex(interpolateColor(startColor, endColor, ratio));
      }
    }

    return colorStops[colorStops.length - 1].color;
  };

  const gotoReport = () => {
    const matchedItem = ctas.find(item => item.ctaRelaTag);
    if (matchedItem?.ctaRelaTagReportId) {
      navigate('/report/' + matchedItem.ctaRelaTagReportId);
    }
  };

  return (
    <div style={{ padding: '0 1.5rem' }}>
      <Row style={{ alignItems: 'center' }}>
        <Col xxl={4} xl={4} lg={4} md={24} sm={24} xs={24}>
          <h3>{t('chat:riskBubble.riskLevel')}</h3>
        </Col>
        <Col xxl={20} xl={20} lg={20} md={24} sm={24} xs={24}>
          <Progress
            percent={100}
            //status="active"
            showInfo={true}
            strokeColor={{
              '0%': '#61C26E',
              '25%': '#E9E138',
              '75%': '#F79122',
              '100%': '#FF0000',
            }}
            format={() => {
              const color = getGradientColorAtPercent((score / 1000) * 100);
              const images = {
                dark: {
                  Minimal: darkLaughHeartily,
                  Low: darkLaugh,
                  Moderate: darkNone,
                  High: darkCry,
                },
                light: {
                  Minimal: lightLaughHeartily,
                  Low: lightLaugh,
                  Moderate: lightNone,
                  High: lightCry,
                },
              };

              const imgurl = images[themeMode]?.[level] || '';
              return (
                <div
                  style={{
                    position: 'absolute',
                    left: `${(score / 1000) * 100}%`,
                    transform: 'translateX(-50%) translateY(-50%)',
                    width: '20px',
                    height: '20px',
                    backgroundColor: color, // 动态背景颜色
                    borderRadius: '50%',
                  }}
                >
                  <img
                    src={imgurl}
                    alt="progress-icon"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </div>
              );
            }}
          />
        </Col>
      </Row>

      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => (
            <span title={href} className="markdown-link">
              {children || href}
            </span>
          ),
          p: ({ children, ...props }) => {
            const processChildren = content => {
              if (typeof content === 'string') {
                const parts = content.split(/(DIAMOND|SHIELD_ICON|CTARELA_TAG)/);
                if (parts.length === 1) {
                  return content;
                }
                return parts.map((part, index) => {
                  if (part === 'DIAMOND') {
                    return (
                      <Tooltip
                        color={'#999999'}
                        key={index}
                        title={t('chat:riskBubble.isWhite')}
                        overlayStyle={{ maxWidth: '600px', whiteSpace: 'normal' }}
                      >
                        <span className="inline-icon-wrapper">
                          <img src={diamondSvg} alt="diamond" className="inline-icon-img" />
                        </span>
                      </Tooltip>
                    );
                  }
                  if (part === 'SHIELD_ICON') {
                    return (
                      <Tooltip
                        color={'#999999'}
                        key={index}
                        title={t('chat:riskBubble.pii')}
                        overlayStyle={{ maxWidth: '600px', whiteSpace: 'normal' }}
                      >
                        <span className="inline-icon-wrapper">
                          <img src={dunpaiSvg} alt="shield" className="inline-icon-img" />
                        </span>
                      </Tooltip>
                    );
                  }
                  if (part === 'CTARELA_TAG') {
                    return (
                      <Tooltip
                        color={'#999999'}
                        key={index}
                        title={t('chat:riskBubble.ctaRelaTag')}
                        overlayStyle={{ maxWidth: '600px', whiteSpace: 'normal' }}
                      >
                        <span className="inline-icon-wrapper">
                          <Button
                            color="cyan"
                            variant="solid"
                            className="inline-cta-button"
                            data-testid="scams-report-risk-bubble-reported-button"
                            onClick={() => gotoReport(parts[0])}
                          >
                            {t('translation:reported')}
                          </Button>
                        </span>
                      </Tooltip>
                    );
                  }
                  return part;
                });
              }
              if (Array.isArray(content)) {
                return content.map((item, index) => (
                  <React.Fragment key={index}>{processChildren(item)}</React.Fragment>
                ));
              }
              if (typeof content === 'object' && content !== null) {
                return content;
              }
              return content;
            };

            return <p {...props}>{processChildren(children)}</p>;
          },
        }}
      >
        {content}
      </ReactMarkdown>

      <Row justify="end">
        {!reportShowTag && isComplete && score >= 500 && Array.isArray(ctas) && ctas.length > 0 && (
          <Space>
            {/* report button */}
            <Button
              type="primary"
              data-testid="scams-report-risk-bubble-report-button"
              style={
                isReevaluate == 1
                  ? { backgroundColor: '#706f6fbe' }
                  : { backgroundColor: '#f7bd40' }
              }
              onClick={handleReport}
              disabled={isReevaluate == 1}
              loading={loading}
            >
              {t('translation:report')}
            </Button>
            <Popover
              content={reportPopoverContent}
              placement="topRight"
              overlayInnerStyle={{
                backgroundColor: '#9a9999',
                borderRadius: '10px',
                padding: '12px 16px',
                color: '#fff',
                maxWidth: isMobile ? 200 : 400,
                fontSize: 14,
                lineHeight: 1.6,
              }}
              arrow={true}
            >
              <div
                style={{
                  width: 23,
                  height: 23,
                  borderRadius: '50%',
                  backgroundColor: '#357E7F',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: '"Playfair Display", serif',
                  fontSize: 15,
                  fontWeight: 'bold',
                }}
              >
                i
              </div>
            </Popover>
          </Space>
        )}
      </Row>

      <Modal
        title={
          <div style={{ textAlign: 'center' }}>
            {t('chat:riskBubble.reportSelectionModal.title')}
          </div>
        }
        open={reportSelectionVisible}
        onOk={handleSelectionOk}
        onCancel={handleSelectionCancel}
        centered={true}
        cancelText={t('translation:cancel')}
        cancelButtonProps={{ shape: 'round', style: { float: 'left', marginLeft: '25%' } }}
        okText={t('translation:report')}
        okButtonProps={{
          'data-testid': 'scams-report-risk-bubble-report-selection-confirm-button',
          shape: 'round',
          style: { marginRight: '25%' },
          disabled: selectedCtas.filter(item => !item.isWhite).length === 0,
        }}
      >
        <Row
          justify="center"
          style={{ textAlign: 'center' }}
          data-testid="scams-report-risk-bubble-report-selection-body"
        >
          {renderSelections()}
        </Row>
      </Modal>

      <Modal
        title={
          <div style={{ textAlign: 'center' }}>
            {getCurrentConversion()
              ? t('chat:riskBubble.ChangeStatementModal.title')
              : t('chat:riskBubble.disclaimerModal.title')}
          </div>
        }
        open={isModalOpen}
        onCancel={handleCancel}
        centered={true}
        footer={
          <div style={{ display: 'flex', justifyContent: 'center', gap: '40px' }}>
            <Button shape="round" onClick={handleCancel}>
              {t('translation:cancel')}
            </Button>
            <Button
              shape="round"
              type="primary"
              onClick={handleOk}
              data-testid="scams-report-risk-bubble-disclaimer-confirm-button"
            >
              {t('translation:understood')}
            </Button>
          </div>
        }
      >
        <Row justify="center" style={{ textAlign: 'center' }}>
          <Col>
            {getCurrentConversion()
              ? t('chat:riskBubble.ChangeStatementModal.content')
              : t('chat:riskBubble.disclaimerModal.content')}
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export default RiskBubble;
